import { useState } from 'react';
import { motion } from 'motion/react';
import { Send, Loader2, CheckCircle, ExternalLink } from 'lucide-react';

const SERVICE_OPTIONS = [
  { value: 'website', label: 'Website Development' },
  { value: 'automation', label: 'Business Automation' },
  { value: 'custom', label: 'Custom Web Application' },
  { value: 'ai', label: 'AI Integration / Agent' },
  { value: 'chatbot', label: 'AI Chatbot' },
  { value: 'marketing', label: 'AI Marketing' },
  { value: 'other', label: 'Other / Not sure yet' },
];

/**
 * Guesses the service from keywords found in the session.need string.
 */
function guessService(need = '') {
  const t = need.toLowerCase();
  if (/\bwebsite\b|\bweb\s+design\b|\blanding\b|\becommerce\b/.test(t)) return 'website';
  if (/\bautomat\b|\bcrm\b|\bworkflow\b|\bn8n\b|\bmake\b/.test(t)) return 'automation';
  if (/\bapp\b|\bportal\b|\bdashboard\b|\bsoftware\b/.test(t)) return 'custom';
  if (/\bai\b|\bagent\b|\bllm\b/.test(t)) return 'ai';
  if (/\bchatbot\b|\bbot\b/.test(t)) return 'chatbot';
  if (/\bmarket\b|\bseo\b|\bads?\b|\bcontent\b/.test(t)) return 'marketing';
  return 'other';
}

/**
 * LeadCard — compact in-chat contact form.
 *
 * Props:
 *   session      { name, email, company, need }  pre-fills the form
 *   onSuccess    () => void   called after successful submission
 */
export default function LeadCard({ session = {}, onSuccess }) {
  const [form, setForm] = useState({
    firstName: session.name ? session.name.split(' ')[0] : '',
    lastName: session.name ? session.name.split(' ').slice(1).join(' ') : '',
    email: session.email || '',
    service: guessService(session.need),
    message: session.need || '',
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | submitting | done | error

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  function validate() {
    const errs = {};
    if (!form.firstName.trim()) errs.firstName = 'Required';
    if (!form.lastName.trim()) errs.lastName = 'Required';
    if (!form.email.trim()) errs.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Invalid email';
    if (!form.message.trim()) errs.message = 'Please describe your project';
    return errs;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setStatus('submitting');

    const body = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      service: form.service,
      message: `[Chatbot Lead]\nService discussed: ${form.service}\n\n${form.message.trim()}`,
      source: 'chat',
    };

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setStatus('done');
      if (onSuccess) setTimeout(onSuccess, 2000);
    } catch (err) {
      console.error('[LeadCard] submit error:', err.message);
      setStatus('error');
    }
  }

  /* --- Styles (inline, so no Tailwind dependency for this inner component) --- */
  const inputBase = {
    width: '100%',
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '10px',
    padding: '8px 12px',
    color: '#fff',
    fontSize: '13px',
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  };
  const labelStyle = { fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginBottom: '4px', display: 'block' };
  const errorStyle = { fontSize: '11px', color: '#f87171', marginTop: '3px' };

  if (status === 'done') {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        style={{
          background: 'rgba(16,185,129,0.1)',
          border: '1px solid rgba(16,185,129,0.3)',
          borderRadius: '16px',
          padding: '20px',
          textAlign: 'center',
        }}
      >
        <CheckCircle style={{ color: '#34d399', width: 32, height: 32, margin: '0 auto 10px' }} />
        <p style={{ color: '#fff', fontWeight: 600, marginBottom: 4, fontSize: 14 }}>You're all set!</p>
        <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12 }}>
          The EsyAI team will be in touch within 24 hours.
        </p>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        background: 'rgba(124,58,237,0.08)',
        border: '1px solid rgba(124,58,237,0.25)',
        borderRadius: '16px',
        padding: '16px',
      }}
    >
      <p style={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600, fontSize: 13, marginBottom: 12 }}>
        📋 Get a quote from EsyAI
      </p>

      <form onSubmit={handleSubmit} noValidate>
        {/* Name row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
          <div>
            <label style={labelStyle}>First name *</label>
            <input
              id="lc-firstName"
              type="text"
              value={form.firstName}
              onChange={set('firstName')}
              placeholder="Jane"
              style={inputBase}
              autoComplete="given-name"
            />
            {errors.firstName && <p style={errorStyle}>{errors.firstName}</p>}
          </div>
          <div>
            <label style={labelStyle}>Last name *</label>
            <input
              id="lc-lastName"
              type="text"
              value={form.lastName}
              onChange={set('lastName')}
              placeholder="Smith"
              style={inputBase}
              autoComplete="family-name"
            />
            {errors.lastName && <p style={errorStyle}>{errors.lastName}</p>}
          </div>
        </div>

        {/* Email */}
        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>Email *</label>
          <input
            id="lc-email"
            type="email"
            value={form.email}
            onChange={set('email')}
            placeholder="jane@example.com"
            style={inputBase}
            autoComplete="email"
          />
          {errors.email && <p style={errorStyle}>{errors.email}</p>}
        </div>

        {/* Service */}
        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>Service</label>
          <select
            id="lc-service"
            value={form.service}
            onChange={set('service')}
            style={{ ...inputBase, cursor: 'pointer' }}
          >
            {SERVICE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value} style={{ background: '#1a1a2e', color: '#fff' }}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {/* Project details */}
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Project details *</label>
          <textarea
            id="lc-message"
            value={form.message}
            onChange={set('message')}
            placeholder="Tell us a bit about what you need…"
            rows={3}
            style={{ ...inputBase, resize: 'vertical', lineHeight: 1.5 }}
          />
          {errors.message && <p style={errorStyle}>{errors.message}</p>}
        </div>

        {/* GDPR note */}
        <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginBottom: 12, lineHeight: 1.4 }}>
          Your details will be used to respond to your enquiry.{' '}
          <a href="/privacy" style={{ color: 'rgba(167,139,250,0.7)', textDecoration: 'underline' }}>
            Privacy Policy
          </a>
        </p>

        {status === 'error' && (
          <p style={{ ...errorStyle, marginBottom: 10 }}>
            Something went wrong. Please try again or email us at info@esyai.co.uk.
          </p>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            id="lc-submit"
            type="submit"
            disabled={status === 'submitting'}
            style={{
              flex: 1,
              background: status === 'submitting' ? 'rgba(124,58,237,0.4)' : 'linear-gradient(135deg,#7c3aed,#4f46e5)',
              border: 'none',
              borderRadius: '10px',
              padding: '9px 16px',
              color: '#fff',
              fontSize: '13px',
              fontWeight: 600,
              cursor: status === 'submitting' ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              transition: 'opacity 0.2s',
            }}
          >
            {status === 'submitting' ? (
              <>
                <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} />
                Sending…
              </>
            ) : (
              <>
                <Send style={{ width: 14, height: 14 }} />
                Send to EsyAI
              </>
            )}
          </button>

          <a
            href="/contact"
            style={{
              fontSize: 11,
              color: 'rgba(167,139,250,0.7)',
              textDecoration: 'underline',
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center',
              gap: 3,
            }}
          >
            Full page <ExternalLink style={{ width: 10, height: 10 }} />
          </a>
        </div>
      </form>
    </motion.div>
  );
}
